import { css, StyleSheet } from 'aphrodite';
import React, { useMemo } from 'react';

import { ActionDef } from '@/features/org/utils/action-catalogue/actionCatalogue';
import { ScopeSelection, SpecificApp } from '@/features/org/utils/scope-arn/scopeArn';

/**
 * Inline scope picker — renders inside the PolicyEditor when an action
 * is checked. Two radio modes:
 *
 *   • All current + future  → matches every (app, env) under the org,
 *                             including resources created later.
 *   • Specific              → multi-select apps (and per-app envs for
 *                             app+env-axis actions).
 *
 * Each "specific app" row also has an "All envs (current + future)"
 * toggle so an admin can grant "any env on these two apps" without
 * having to pick envs by hand.
 *
 * Org-axis actions (orgs:*, users:*, roles:assign:*, apps:create,
 * github:install) skip this control entirely — they're always org-wide.
 */
export default function ScopePicker({
  action,
  selection,
  apps,
  envsByAppId,
  onChange,
}: {
  action: ActionDef;
  selection: ScopeSelection;
  apps: { id: string; name: string }[];
  envsByAppId: Record<string, { id: string; name: string; kind: string }[]>;
  onChange: (next: ScopeSelection) => void;
}) {
  // Quick lookup so the chip in collapsed mode can read names.
  const appNameById = useMemo(() => new Map(apps.map((a) => [a.id, a.name])), [apps]);

  if (action.dimension === 'org') {
    // Nothing to scope — render a tiny pill so admins see why.
    return <span className={css(sp.orgPill)}>Org-wide</span>;
  }

  const isAll = selection.kind === 'all';
  const specificApps = isAll ? [] : selection.apps;

  const setKind = (kind: 'all' | 'specific') => {
    if (kind === 'all') onChange({ kind: 'all' });
    else onChange({ kind: 'specific', apps: specificApps });
  };

  const toggleApp = (appId: string) => {
    const existing = specificApps.find((a) => a.appId === appId);
    let next: SpecificApp[];
    if (existing) {
      next = specificApps.filter((a) => a.appId !== appId);
    } else {
      // New app: default to "all envs" (matches the "all current+future"
      // ethos at the env level).
      next = [...specificApps, { appId, envs: 'all' as const }];
    }
    onChange({ kind: 'specific', apps: next });
  };

  const setAppEnvsMode = (appId: string, mode: 'all' | 'specific') => {
    const next = specificApps.map((a) =>
      a.appId === appId
        ? { ...a, envs: mode === 'all' ? ('all' as const) : Array.isArray(a.envs) ? a.envs : [] }
        : a,
    );
    onChange({ kind: 'specific', apps: next });
  };

  const toggleEnv = (appId: string, envId: string) => {
    const next = specificApps.map((a) => {
      if (a.appId !== appId) return a;
      const envs = a.envs === 'all' ? [] : a.envs;
      const has = envs.includes(envId);
      return { ...a, envs: has ? envs.filter((e) => e !== envId) : [...envs, envId] };
    });
    onChange({ kind: 'specific', apps: next });
  };

  return (
    <div className={css(sp.root)}>
      <div className={css(sp.modes)}>
        <label className={css(sp.modeRow)}>
          <input
            type="radio"
            name={`scope-${action.id}`}
            checked={isAll}
            onChange={() => setKind('all')}
          />
          <span className={css(sp.modeLabel)}>All current + future</span>
          <span className={css(sp.modeHelp)}>
            {action.dimension === 'app'
              ? 'Every app in this org, including ones created later.'
              : 'Every app + every env, including ones created later.'}
          </span>
        </label>
        <label className={css(sp.modeRow)}>
          <input
            type="radio"
            name={`scope-${action.id}`}
            checked={!isAll}
            onChange={() => setKind('specific')}
          />
          <span className={css(sp.modeLabel)}>Specific resources</span>
          <span className={css(sp.modeHelp)}>
            Pick the apps {action.dimension === 'app+env' ? '+ envs ' : ''}this action applies to.
          </span>
        </label>
      </div>

      {!isAll && (
        <div className={css(sp.appsList)}>
          {apps.length === 0 && (
            <div className={css(sp.empty)}>
              No apps in this org yet. Pick "All current + future" or create an app first.
            </div>
          )}
          {apps.map((app) => {
            const picked = specificApps.find((a) => a.appId === app.id);
            const envs = envsByAppId[app.id] ?? [];
            const envMode = picked?.envs === 'all' ? 'all' : 'specific';
            const pickedEnvIds = picked && picked.envs !== 'all' ? picked.envs : [];

            return (
              <div key={app.id} className={css(sp.appRow)}>
                <label className={css(sp.appHeader)}>
                  <input type="checkbox" checked={!!picked} onChange={() => toggleApp(app.id)} />
                  <span className={css(sp.appName)}>{app.name}</span>
                  <code className={css(sp.appId)}>{app.id}</code>
                </label>

                {picked && action.dimension === 'app+env' && (
                  <div className={css(sp.envsBlock)}>
                    <label className={css(sp.envModeRow)}>
                      <input
                        type="radio"
                        name={`scope-${action.id}-app-${app.id}-env`}
                        checked={envMode === 'all'}
                        onChange={() => setAppEnvsMode(app.id, 'all')}
                      />
                      <span>All envs (current + future)</span>
                    </label>
                    <label className={css(sp.envModeRow)}>
                      <input
                        type="radio"
                        name={`scope-${action.id}-app-${app.id}-env`}
                        checked={envMode === 'specific'}
                        onChange={() => setAppEnvsMode(app.id, 'specific')}
                      />
                      <span>Specific envs</span>
                    </label>
                    {envMode === 'specific' && (
                      <div className={css(sp.envsList)}>
                        {envs.length === 0 && (
                          <div className={css(sp.envEmpty)}>
                            No envs on this app — switch back to "All envs" or create one.
                          </div>
                        )}
                        {envs.map((env) => (
                          <label key={env.id} className={css(sp.envRow)}>
                            <input
                              type="checkbox"
                              checked={pickedEnvIds.includes(env.id)}
                              onChange={() => toggleEnv(app.id, env.id)}
                            />
                            <span className={css(sp.envName)}>{env.name}</span>
                            <span className={css(sp.envKind)}>{env.kind}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const sp = StyleSheet.create({
  root: {
    background: 'var(--td-gray-50, #f9f9f9)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 6,
    padding: 10,
    marginTop: 6,
  },
  modes: { display: 'flex', flexDirection: 'column' as const, gap: 6, marginBottom: 8 },
  modeRow: {
    display: 'grid',
    gridTemplateColumns: '16px auto 1fr',
    columnGap: 8,
    alignItems: 'baseline' as const,
    cursor: 'pointer',
    fontSize: 13,
  },
  modeLabel: { fontWeight: 600, color: 'var(--td-gray-900, #1a1a1a)' },
  modeHelp: { fontSize: 11, color: 'var(--td-gray-500, #777)' },
  appsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    marginTop: 4,
    borderTop: '1px solid var(--td-gray-100, #f3f3f3)',
    paddingTop: 8,
  },
  appRow: {
    background: 'var(--td-white, #fff)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 6,
    padding: '8px 10px',
  },
  appHeader: {
    display: 'grid',
    gridTemplateColumns: '16px 1fr auto',
    columnGap: 8,
    alignItems: 'center' as const,
    cursor: 'pointer',
    fontSize: 13,
  },
  appName: { fontWeight: 600 },
  appId: { fontSize: 10, color: 'var(--td-gray-500, #777)', fontFamily: 'monospace' },
  envsBlock: {
    marginTop: 6,
    paddingLeft: 24,
    borderLeft: '2px solid var(--td-gray-100, #f3f3f3)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  envModeRow: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: 6,
    cursor: 'pointer',
    fontSize: 12,
  },
  envsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    marginTop: 4,
    paddingLeft: 18,
  },
  envRow: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr auto',
    columnGap: 6,
    alignItems: 'center' as const,
    cursor: 'pointer',
    fontSize: 12,
  },
  envName: {},
  envKind: {
    fontSize: 10,
    textTransform: 'uppercase' as const,
    color: 'var(--td-gray-500, #777)',
    background: 'var(--td-gray-50, #f9f9f9)',
    padding: '1px 6px',
    borderRadius: 4,
  },
  envEmpty: { fontSize: 11, color: 'var(--td-gray-500, #777)', padding: '4px 0' },
  empty: { fontSize: 12, color: 'var(--td-gray-500, #777)' },
  orgPill: {
    display: 'inline-block',
    fontSize: 11,
    background: 'var(--td-gray-100, #f3f3f3)',
    color: 'var(--td-gray-500, #777)',
    padding: '2px 8px',
    borderRadius: 4,
  },
});
